/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Pie } from '@antv/g2plot';

import Frame from '../frame';
import Style from './style';
import Metrics from '../metrics';
import { actions } from '../../../../../../../store/reducers/dashboard';
import { useDispatch } from 'react-redux';

import { getMetricsValues } from './helper';
import { StakeholdersDistributionTooltip } from '../../../../../../../helpers/Charts';
import { useGeneralContext } from '../../../../../../../context/General';

const StakeholdersDistribution = ({
  t = (t) => t,
  data = null,
  language,
  canFetch = false,
  selectedProject,
  sourceId,
}) => {
  const { theme } = useGeneralContext();
  const [graph, setGraph] = useState(null);
  const containerRef = useRef();
  const isEmpty = useMemo(() => data ? data.total > 0 ? false : true : true, [data]);
  const dispatch = useDispatch();
  const metricsValues = useMemo(() => getMetricsValues(theme), [theme]);

  useEffect(() => {
    if (canFetch) {
      dispatch(actions.PmeDashboardGetWithFilters({
        widget: 'distributionOfStakeholders',
      }));
    }
  }, [language, canFetch, selectedProject, sourceId]);

  const [frameActions, setFrameActions] = useState(null);

  const sourceOfData = useMemo(() => {
    if (data) {
      const d = metricsValues.map((m) => data[m.key]
        ? ({
          type: m.label,
          value: Math.round((data[m.key]) / data.total * 100),
          color: m.color,
          realValue: data[m.key],
        }) : false
      ).filter((m) => m);
      return d;
    }
    return null;
  }, [data, metricsValues]);

  useEffect(() => {
    if (frameActions) {
      if (data) {
        frameActions.loading(false);
      } else {
        frameActions.loading(true);
      }
    }
  }, [frameActions, data]);

  useEffect(() => {
    if (!graph && metricsValues && sourceOfData) {
      const piePlot = new Pie(containerRef.current, {
        appendPadding: 10,
        data: isEmpty ? [{ type: "Empty", value: 0 }] : sourceOfData,
        angleField: 'value',
        colorField: 'type',
        radius: 1,
        innerRadius: 0.6,
        label: {
          type: 'inner',
          offset: '-50%',
          autoRotate: false,
          style: { textAlign: 'center', fontWeight: 600 },
          content: isEmpty ? '' : '{value} %',
        },
        tooltip: isEmpty ? false : {
          customContent: (_, data) => StakeholdersDistributionTooltip(data, t),
        },
        legend: false,
        statistic: {
          title: false,
          content: {
            style: {
              whiteSpace: 'wrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
            formatter: () => `
              <div>
                <p style="font-size: 30px; margin-bottom: 2px; color: var(--mmt-primary-70);">${isEmpty ? '0' : data.total}</p>
                <p style="font-size: 12px; font-weight: 400; margin-bottom: 0px; color: #595959">${t('Identified stakeholders')}</p>
              </div>`,
          },
        },
        color: isEmpty ? ['#EDEDED'] : sourceOfData.map((d) => d.color),
      });
      setGraph(piePlot);
      piePlot.render();
    } else if (graph && sourceOfData) {
      graph.update({
        data: isEmpty ? [{ type: "Empty", value: 0 }] : sourceOfData,
        color: isEmpty ? '#EDEDED' : sourceOfData.map((d) => d.color),
        tooltip: isEmpty ? false : {
          customContent: (_, data) => StakeholdersDistributionTooltip(data, t),
        },
        statistic: {
          title: false,
          content: {
            style: {
              whiteSpace: 'wrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
            formatter: () => `
              <div>
                <p style="font-size: 30px; margin-bottom: 2px; color: var(--mmt-primary-70);">${isEmpty ? '0' : data.total}</p>
                <p style="font-size: 12px; font-weight: 400; margin-bottom: 0px; color: #595959">${t('Identified stakeholders')}</p>
              </div>`,
          },
        },
        label: {
          type: 'inner',
          offset: '-50%',
          autoRotate: false,
          style: { textAlign: 'center', fontWeight: 600 },
          content: isEmpty ? '' : '{value} %',
        },
      });
    }
  }, [metricsValues, data]);

  return <Frame
    onInit={actions => setFrameActions(actions)}
    title={t('Distribution')}
    className="h-full"
  >
    <Style>
      <div className="chart-container">
        <div ref={containerRef} />
      </div>
      <Metrics
        t={t}
        metricsValues={metricsValues}
        isEmpty
        roundedIdentifiers
        noBorders
        distributionStakeholders
      />
    </Style>
  </Frame>;
}

export default StakeholdersDistribution;
